import {Box} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {scaleBand, scaleLinear} from 'd3'
import type {ScaleTime} from 'd3'
import type {FC} from 'react'
import {useEffect, useMemo, useState} from 'react'
import D3DailyProductionBarTooltip from './D3DailyProductionBarTooltip'
import {getColorFromProgramClassification} from '@app/util-functions'
import LinearYAxis from '@shared/components/d3Components/LinearYAxis'
import {useProgramClassifications} from '@shared/hooks/useProgramClassifications'
import D3ProductionTimelineRect from '@shared/components/d3Components/productionTimeline/ProductionTimelineRect'
import TimeXAxis from '@shared/components/d3Components/TimeXAxis'
import {programClassifications} from '@shared/constants/programClassifications'

interface TransformedData {
  date: string
  [classification: string]: number | string | object // Additional dynamic keys for classifications
}

interface AvailabilityData {
  date: string
  classification: string
  duration: number
  id: string
  unused_capacity?: {
    [key: string]: number
  } | null
}

interface D3StackedBarChartProps {
  data: AvailabilityData[]
  width: number
  height: number
  margin: {
    top: number
    right: number
    bottom: number
    left: number
  }
  summarizeBy?: string
  tooltip?: boolean
}

interface D3DailyProductionBarTooltipProps {
  open: boolean
  color: string
  seriesKey: string
  value: number
  percentage: number
  unusedCapacity: {
    [key: string]: number
  } | null
}

type ProgramClassification = (typeof programClassifications)[number]

const D3DailyProductionBar: FC<D3StackedBarChartProps> = ({
  data,
  width,
  height,
  summarizeBy,
  tooltip,
  margin,
}) => {
  const theme = useTheme()
  const {classifications} = useProgramClassifications()
  const [tooltipData, setTooltipData] = useState<D3DailyProductionBarTooltipProps>({
    open: false,
    color: '',
    seriesKey: '',
    value: 0,
    percentage: 0,
    unusedCapacity: null,
  })
  const [transformedData, setTransformedData] = useState<TransformedData[]>([])

  useEffect(() => {
    const reducedData = data.reduce((acc, curr) => {
      const existingDay = acc.find((day) => day.date === curr.date)
      if (existingDay) {
        existingDay[curr.classification] = curr.duration
        if (curr.classification === 'downtime' && curr.unused_capacity) {
          existingDay[`unusedCapacity_${curr.classification}`] = curr.unused_capacity
        }
      } else {
        acc.push({
          date: curr.date,
          [curr.classification]: curr.duration,
        })
      }
      return acc
    }, [] as TransformedData[])
    setTransformedData(reducedData)
  }, [data])

  const xScale = scaleBand()
    .domain(transformedData.map((d) => d.date))
    .range([0, width])
    .padding(0.3)

  const yScale = scaleLinear()
    .domain(summarizeBy === 'week' ? [0, 168] : [0, 24])
    .range([height, 0])

  const getUniqueKeysWithoutDate = (dataArray: TransformedData[]): string[] => {
    const uniqueKeys = new Set<string>() // Define the type of elements in the Set explicitly

    dataArray.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'date') {
          uniqueKeys.add(key)
        }
      })
    })

    return Array.from(uniqueKeys)
  }

  const batchKeys = useMemo(
    () =>
      getUniqueKeysWithoutDate(transformedData).sort((a, b) => {
        const indexA = programClassifications.indexOf(a as ProgramClassification)
        const indexB = programClassifications.indexOf(b as ProgramClassification)

        // If both keys are found in programClassifications, sort by their order
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }

        // If only one key is found, prioritize it
        if (indexA !== -1) {
          return -1
        }
        if (indexB !== -1) {
          return 1
        }

        // If neither key is found, maintain original order (or add other criteria)
        return 0
      }),
    [transformedData],
  )

  const onMouseEnterHandler = (key: string, data: TransformedData): void => {
    const sum = Object.keys(data)
      .filter((k) => k !== 'date' && typeof data[k] === 'number')
      .reduce((total, k) => total + (data[k] as number), 0)
    const rawValue = data[key] as number // Ensure this is a number
    const percentage = (rawValue / sum) * 100
    const unusedCapacity =
      key === 'downtime' ? (data.unusedCapacity_downtime as {[key: string]: number}) : null

    setTooltipData({
      open: true,
      color: getColorFromProgramClassification(key, theme, classifications),
      seriesKey: key,
      value: rawValue,
      percentage: parseFloat(percentage.toFixed(1)),
      unusedCapacity,
    })
  }

  const onMouseLeaveHandler = (): void => {
    setTooltipData({
      open: false,
      color: '',
      seriesKey: '',
      value: 0,
      percentage: 0,
      unusedCapacity: null,
    })
  }

  const tickValuesDay = [0, 4, 8, 12, 16, 20, 24]
  const tickValuesWeek = [0, 24, 48, 72, 96, 120, 144, 168]

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
      data-testid="mbai-d3daily-production-bar"
    >
      <svg width="100%" height="100%">
        <g className="x-axis" transform={`translate(${margin.left}, ${margin.top + height})`}>
          <TimeXAxis
            width={width}
            xScale={xScale as unknown as ScaleTime<number, number>}
            height={height}
            displayInUTC
            summarizeBy={summarizeBy}
          />
        </g>
        <g className="y-axis" transform={`translate(${margin.left}, ${margin.top})`}>
          <LinearYAxis
            yScale={yScale}
            width={width}
            tickValues={summarizeBy === 'day' ? tickValuesDay : tickValuesWeek}
            tickFormat
          />
        </g>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {transformedData.map((d) => {
            let cumulativeHeight = 0
            return batchKeys.map((key) => {
              if (d[key]) {
                const value = d[key] as number
                const rectHeight = yScale(0) - yScale(value) // Height of the current segment
                const y = yScale(cumulativeHeight + value) // Y-position of the current segment
                cumulativeHeight += value // Update the cumulative height
                return (
                  <D3ProductionTimelineRect
                    key={`${d.date}-${key}`}
                    keyProp={`${d.date}-${key}`}
                    mainKey={key as string}
                    x={xScale(d.date as unknown as string) as number}
                    y={y}
                    width={xScale.bandwidth() as number}
                    rectHeight={rectHeight}
                    fill={getColorFromProgramClassification(key, theme, classifications)}
                    hours={value}
                    onMouseEnter={() => onMouseEnterHandler(key, d)}
                    onMouseLeave={onMouseLeaveHandler}
                  />
                )
              }
              return null
            })
          })}
        </g>
      </svg>
      {tooltip ? <D3DailyProductionBarTooltip {...tooltipData} /> : null}
    </Box>
  )
}

export default D3DailyProductionBar
